/**
 * SupportPage — ticket hub
 *
 * Clean landing page for users who need help.
 * Shows "Who Can Help" (with Link buttons to raise tickets) and
 * "Help Us, Help You - Raising a good ticket".  No Q&A / browse — that lives in FAQsPage.
 */

import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DescriptionIcon from '@mui/icons-material/Description';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ArticleIcon from '@mui/icons-material/Article';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Page, Header, Content } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';

// ── Hardcoded team signposting ────────────────────────────────────────────────

interface TeamLink {
  label: string;
  url: string;
  tooltip?: string;
  type?: 'ticket' | 'qa' | 'info';
}

interface Team {
  name: string;
  description: string;
  detail: string;
  links: TeamLink[];
}

const TEAMS: Team[] = [
  {
    name: 'Agentic Toolchain',
    description: 'Local Toolkit, OneStack & Agentic SDLC',
    detail:
      'Builds and maintains The Local Toolkit and OneStack.\n\nDefines the agentic SDLC and builds the tooling, standards, and decisions that support it — recording architecture decisions, publishing patterns teams can build on with confidence, and working directly with engineering teams to help them adopt agentic tooling well.',
    links: [
      {
        label: 'Raise a Ticket',
        url: 'https://github.com/advancedcsg/agentic-toolchain/issues/new/choose',
        tooltip: 'Opens a GitHub issue for Agentic Toolchain support',
        type: 'ticket' as const,
      },
      {
        label: 'Escalate Issue',
        url: 'https://idp.oneadvanced.io/docs/default/component/agentic-toolchain-documentation/support/',
        tooltip: 'Agentic Toolchain escalation guidance',
        type: 'info' as const,
      },
    ],
  },
  {
    name: 'Toolchain',
    description: 'Tools, Governance & Access Control',
    detail:
      'Owns and operates the toolchain: Advanced Artefacts, AWS & Azure access, GitHub, Harness, Internal Developer Portal, SonarQube etc.\n\nAlso responsible for cloud governance & cost management, security & compliance automation, CI/CD golden paths, toolchain infrastructure, FinOps reporting and more.',
    links: [
      {
        label: 'Raise a Ticket',
        url: 'https://oneadvanced.service-now.com/esc?id=sc_cat_item&sys_id=941b8637fb81ee10b4edf2c1aeefdce2',
        tooltip: 'Opens a ServiceNow request for Toolchain support',
        type: 'ticket' as const,
      },
      {
        label: 'Escalate Issue',
        url: 'https://idp.oneadvanced.io/faqs?section=toolchain&category=general&entry=how-do-i-escalate-an-issue-with-the-toolchain-1b6ynso',
        tooltip: 'How to escalate a Toolchain issue',
        type: 'qa' as const,
      },
    ],
  },
  {
    name: 'Operations',
    description: 'Incidents & Infrastructure',
    detail:
      'Handles all BAU and interrupt-driven work: incident management, infrastructure patching, cost optimisation, security remediation, compliance, customer provisioning, and go-to-market readiness.',
    links: [
      {
        label: 'Raise a Ticket',
        url: 'https://oneadvanced.service-now.com/esc?id=sc_cat_item&sys_id=a3bf03923b11aa9057526caf55e45a49',
        tooltip: 'Opens a ServiceNow request for Operations support',
        type: 'ticket' as const,
      },
      {
        label: 'Escalate Issue',
        url: 'https://idp.oneadvanced.io/faqs?section=operations&category=general&entry=how-do-i-escalate-an-operations-issue-1h7avgw',
        tooltip: 'How to escalate an Operations issue',
        type: 'qa' as const,
      },
    ],
  },
  {
    name: 'Site Reliability Engineering (SRE)',
    description: 'Reliability, automation & engineering',
    detail:
      'Focused on removing toil and improving reliability: automation, SRE principles adoption, IaC, CI/CD pipelines, scalability, and proactive measures that prevent operational load from growing.',
    links: [
      {
        label: 'View Team',
        url: 'https://advancedcsg.atlassian.net/wiki/spaces/plateng/pages/5629313080/SRE+DevOps+Structure',
        tooltip: 'Opens the SRE & DevOps team structure on Confluence',
        type: 'info' as const,
      },
      {
        label: 'Escalate Issue',
        url: 'https://idp.oneadvanced.io/faqs?section=site-reliability-engineering-sre&category=general&entry=how-do-i-escalate-an-sre-issue-04sdgok',
        tooltip: 'How to escalate an SRE issue',
        type: 'qa' as const,
      },
    ],
  },
];

const ETIQUETTE_TIPS = [
  "No ticket, no action — for anything that needs actioning, a ticket is the only way to ensure it's tracked and resolved.",
  "Don't raise the same issue across multiple channels or open duplicate tickets — it splits the conversation and slows resolution down for everyone.",
  "When following up on an existing ticket, lead with the ticket reference and provide relevant context — not just \"Hi\". It will help us respond faster.",
  "Not everything is P1 — be honest about your urgency so the team can triage fairly for everyone.",
  'If your issue gets resolved, close your ticket or let the team know — it keeps the queue clean and accurate.',
  'Be patient and respectful — the team is juggling multiple priorities and doing their best to help you.',
];

const TICKET_TIPS = [
  "The quality of your ticket impacts the speed of your resolution — a clear, detailed ticket typically gets resolved faster.",
  "Give the ticket a clear, specific title — avoid vague titles like \"it's broken\".",
  'Describe what you did and what you expected to happen.',
  'Include the actual result and any error messages or logs.',
  'Attach screenshots, terminal output, or log snippets where possible.',
  'State the impact: who is affected, how urgently you need a resolution, and your environment (prod/staging/local).',
  'Provide context: repository name, tool version, and when the issue started.',
];

function getLinkIcon(type: TeamLink['type']) {
  if (type === 'qa') return <QuestionAnswerIcon />;
  if (type === 'info') return <ArticleIcon />;
  return <ConfirmationNumberIcon />;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  sectionHeader: {
    fontWeight: 700,
    fontSize: 20,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  teamCard: {
    borderRadius: 8,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.15s',
    '&:hover': { boxShadow: theme.shadows[6] },
  },
  teamCardContent: {
    padding: theme.spacing(2.5),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': { paddingBottom: theme.spacing(2.5) },
  },
  teamName: {
    fontWeight: 700,
    fontSize: 16,
    marginBottom: theme.spacing(0.5),
  },
  teamDesc: {
    fontSize: 13,
    fontStyle: 'italic',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  teamDivider: {
    marginBottom: theme.spacing(1.5),
  },
  teamDetail: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
    flex: 1,
    whiteSpace: 'pre-line',
  },
  tipsCard: {
    borderRadius: 8,
    padding: theme.spacing(2.5),
    height: '100%',
  },
  cardSubheading: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tipItem: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    '&:last-child': { marginBottom: 0 },
  },
  tipNumber: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    minWidth: 20,
    flexShrink: 0,
  },
}));

// ── Main page ─────────────────────────────────────────────────────────────────

export function SupportPage() {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <Page themeId="tool">
      <Header
        title="Support"
        subtitle="Need help? Find the right team and get the support you need to keep moving forward."
      />
      <Content>
        {/* ── Looking for Answers? ── */}
        <Typography className={classes.sectionHeader} sx={{ mt: '0 !important' }}>Looking for Answers?</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: '#4CAF50',
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(76,175,80,0.08)' : '#f1f8f1',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Start Here — Docs &amp; FAQs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Before reaching out, check our documentation and frequently asked questions.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                Hopefully your question has been asked before and already has an answer ready to go!
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" startIcon={<DescriptionIcon />} onClick={() => navigate('/docs')}>
                  Browse Docs
                </Button>
                <Button variant="contained" size="small" startIcon={<QuestionAnswerIcon />} onClick={() => navigate('/faqs')}>
                  Browse FAQs
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: '#FFC107',
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,193,7,0.08)' : '#fffde7',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Have a Question or Need Some Advice?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Jump into the <strong>SRE & DevOps Community - Q&amp;A channel</strong> — no ticket needed.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                The community will try to help or point you in the right direction. For anything that needs actioning, a ticket will still be required — see the next column.
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<QuestionAnswerIcon />}
                  href="https://teams.microsoft.com/l/channel/19%3Aea0395adce234596a3fff13eb1f718d7%40thread.tacv2/Questions%20and%20Answers?groupId=f7a4ad0b-b562-4ff0-a64f-81fff30b2ec1&tenantId=2b5a89e7-01bf-42ed-9da1-669d82eec17a"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Teams Q&amp;A Channel
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: '#F44336',
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.08)' : '#fff5f5',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Have an Issue and Need Help?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Find the right team and raise a ticket — no ticket means no action is taken.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                Check the tips below before raising one — a well-written ticket gets resolved faster.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" startIcon={<KeyboardArrowDownIcon />} href="#who-can-help" onClick={e => { e.preventDefault(); document.getElementById('who-can-help')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Who Can Help
                </Button>
                <Button variant="contained" size="small" startIcon={<KeyboardArrowDownIcon />} href="#help-us-help-you" onClick={e => { e.preventDefault(); document.getElementById('help-us-help-you')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  Help Us, Help You
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* ── Who Can Help ── */}
        <Typography id="who-can-help" className={classes.sectionHeader}>Who Can Help</Typography>

        <Grid container spacing={3}>
          {TEAMS.map(team => (
            <Grid item xs={12} md={3} key={team.name}>
              <Card className={classes.teamCard} variant="outlined">
                <CardContent className={classes.teamCardContent}>
                  <Typography className={classes.teamName}>{team.name}</Typography>
                  <Typography className={classes.teamDesc}>
                    {team.description}
                  </Typography>
                  <Divider className={classes.teamDivider} />
                  <Typography className={classes.teamDetail}>
                    {team.detail}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {team.links.map(link => (
                      <Tooltip key={link.label} title={link.tooltip ?? ''} arrow disableHoverListener={!link.tooltip}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={getLinkIcon(link.type)}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.label}
                        </Button>
                      </Tooltip>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Help Us, Help You ── */}
        <Typography id="help-us-help-you" className={classes.sectionHeader}>Help Us, Help You</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.tipsCard} variant="outlined">
              <Typography className={classes.cardSubheading}>Working With Us Effectively</Typography>
              {ETIQUETTE_TIPS.map((tip, i) => (
                <div key={i} className={classes.tipItem}>
                  <span className={classes.tipNumber}>{i + 1}.</span>
                  <Typography variant="body2">{tip}</Typography>
                </div>
              ))}
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card className={classes.tipsCard} variant="outlined">
              <Typography className={classes.cardSubheading}>Raising a Good Ticket</Typography>
              {TICKET_TIPS.map((tip, i) => (
                <div key={i} className={classes.tipItem}>
                  <span className={classes.tipNumber}>{i + 1}.</span>
                  <Typography variant="body2">{tip}</Typography>
                </div>
              ))}
            </Card>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}
